/**
 * The broadcast draft: the shape the composer edits, plus the pure helpers
 * that derive from it.
 *
 * Deliberately free of API field names — everything here is camelCase UI
 * vocabulary. `broadcastPayload.js` translates this into request fields and
 * `broadcastDecode.js` translates responses back; those two are the only
 * modules that know what the wire calls anything.
 *
 * PURE: no React, no localStorage, no `Date.now()`. `validateDraft` takes
 * `now` by injection so scheduling checks are testable without faking time.
 */
import {
  BODY_MAX,
  DEFAULT_COMPOSE_MODE,
  DEFAULT_EMAIL_SEGMENT,
  DEFAULT_PUSH_SEGMENT,
  DEFAULT_ROUTE,
  ROUTE_BY_ID,
  TITLE_MAX,
  channelUsesEmail,
  channelUsesPush,
  isComposeMode,
  isEmailSegment,
  isPushSegment,
  segmentNeedsFilter,
} from "./broadcastEnums";
import { isEmailBodyEmpty } from "./emailBodyHtml";
import { toDatetimeLocal } from "./broadcastTime";

/**
 * THE TWO CONTENT BLOCKS BELOW DO NOT TALK TO EACH OTHER.
 *
 * `title`/`body`/`imageUrl`/`routeKey` belong to the popup. `emailSubject`/
 * `emailBodyHtml`/the CTA pair belong to the email. Nothing falls back from one
 * to the other, in this file or in any other: the dormant block is untouched,
 * unvalidated and unsent.
 *
 * That is the whole point of the split. When the email subject defaulted to the
 * push title, "leave it empty" was a real and invisible state — an author could
 * not tell whether the blank field meant "inherit" or "forgot", and neither
 * could anyone reading the draft afterwards.
 *
 * @typedef {typeof EMPTY_DRAFT} BroadcastDraft
 */
export const EMPTY_DRAFT = {
  // 1 · Which of the two things is being written. Picked first; decides
  //     everything below it.
  channels: DEFAULT_COMPOSE_MODE,

  /* ── Popup ──────────────────────────────────────────────────────────── */
  title: "",
  body: "",
  /** The value sent as the popup image. Empty string means "no image". */
  imageUrl: "",
  /**
   * The storage path the same upload returned. Kept alongside `imageUrl`
   * purely so the console can render a preview: the upload URL is private and
   * a direct GET is refused, so previews go through the media-url endpoint
   * keyed on this path. Never sent.
   */
  imageBlobPath: "",
  /** Where a tap lands — one of six closed values. No custom link exists. */
  routeKey: DEFAULT_ROUTE,
  pushSegment: DEFAULT_PUSH_SEGMENT,
  /** Set only when `pushSegment` is `filtered`. */
  pushFilterId: "",

  /* ── Email ──────────────────────────────────────────────────────────── */
  emailSubject: "",
  /**
   * The body, as markup. The only field that carries the email's copy — the
   * inbox preview line the wire wants as `notification_body` is derived from
   * this rather than asked for separately, and there is no email image: see the
   * header note on `EmailContentCard`.
   */
  emailBodyHtml: "",
  emailCtaText: "",
  emailCtaUrl: "",
  emailSegment: DEFAULT_EMAIL_SEGMENT,
  /** Set only when `emailSegment` is `filtered`. */
  emailFilterId: "",

  /* ── Schedule — the one thing both modes share ──────────────────────── */
  scheduleMode: "now",
  scheduledAtLocal: "",
};

/**
 * Does this draft send a push? An email? Exactly one is true for anything the
 * composer can build; both stay defined because a decoded legacy `both` record
 * still has to answer them.
 */
export const usesPush = (draft) => channelUsesPush(draft?.channels);
export const usesEmail = (draft) => channelUsesEmail(draft?.channels);

/**
 * The image field belonging to the mode being composed.
 *
 * Only the popup has one. An email draft answers "" — not because its image is
 * empty, but because an email has no image to attach.
 */
const imageUrlFor = (draft) =>
  usesEmail(draft) ? "" : String(draft?.imageUrl || "").trim();

/** Has the author attached an image to the surface they are writing? */
export const hasImage = (draft) => Boolean(imageUrlFor(draft));

/**
 * The deep link the chosen destination resolves to, for display under the
 * picker. Display only — the payload sends the route id, not this string.
 *
 * @param {BroadcastDraft} draft
 * @returns {string} e.g. "flex://workouts", or "" for an unknown route.
 */
export const resolveDeepLink = (draft) =>
  ROUTE_BY_ID[draft?.routeKey]?.deepLink || "";

/**
 * Build a draft from a decoded row (camelCase, from `broadcastDecode.js`).
 * Takes the DECODED shape rather than the raw record on purpose, so this file
 * never has to name a wire field.
 *
 * @param {Object} row
 * @returns {BroadcastDraft}
 */
export const draftFromRow = (row = {}) => {
  // A legacy `both` record opens as a popup rather than as a mode the composer
  // cannot represent. Reopening it does not re-send it — this is a starting
  // point for a new broadcast, and the mode is the first thing on the screen.
  const channels = isComposeMode(row.channels)
    ? row.channels
    : DEFAULT_COMPOSE_MODE;
  // The row's headline and supporting line are whichever mode wrote them, so
  // they are restored into that mode's fields. Loading an email back into the
  // popup's title box would put the copy where the composer cannot show it and
  // will not send it.
  const email = channelUsesEmail(channels);

  return {
    ...EMPTY_DRAFT,
    title: email ? "" : row.title || "",
    body: email ? "" : row.body || "",
    imageUrl: email ? "" : row.imageUrl || "",
    imageBlobPath: "",
    emailSubject: email ? row.title || "" : "",
    // The row's `body` is the derived preview line, not copy anyone wrote — it
    // is regenerated from `emailBodyHtml` on the way back out, so restoring it
    // into a field would duplicate the body's opening words into the draft.
    emailBodyHtml: email ? row.emailBodyHtml || "" : "",
    routeKey: ROUTE_BY_ID[row.routeKey] ? row.routeKey : DEFAULT_ROUTE,
    channels,
    pushSegment: isPushSegment(row.pushSegment)
      ? row.pushSegment
      : DEFAULT_PUSH_SEGMENT,
    emailSegment: isEmailSegment(row.emailSegment)
      ? row.emailSegment
      : DEFAULT_EMAIL_SEGMENT,
    scheduleMode: row.scheduledAt ? "later" : "now",
    // Restore the actual instant, not just the mode. Setting `later` with a
    // blank time produced a draft that `validateDraft` immediately rejected
    // with "Pick a date and time" — losing a time the record already had, and
    // blaming the user for it.
    scheduledAtLocal: toDatetimeLocal(row.scheduledAt),
  };
};

/**
 * Validate a draft.
 *
 * @param {BroadcastDraft} draft
 * @param {{ now?: Date }} [options]
 * @returns {Array<{ field: string, message: string }>} Empty when valid.
 */
export const validateDraft = (draft, { now = new Date() } = {}) => {
  const errors = [];
  const push = (field, message) => errors.push({ field, message });

  /**
   * A required line of copy, with the same two complaints everywhere.
   *
   * @param {string} field - Draft field name, e.g. "emailSubject".
   * @param {string} noun - How the message names it, e.g. "Subject".
   * @param {number} max - `TITLE_MAX` or `BODY_MAX`.
   * @param {string} empty - The message when nothing is written.
   */
  const requireText = (field, noun, max, empty) => {
    const value = String(draft?.[field] || "").trim();
    if (value.length === 0) {
      push(field, empty);
    } else if (value.length > max) {
      push(field, `${noun} is ${value.length} characters — the limit is ${max}.`);
    }
  };

  if (!isComposeMode(draft?.channels)) {
    push("channels", "Pick a popup or an email.");
  }

  // ONE BRANCH RUNS. The dormant mode's fields are not validated, because
  // they are not sent: blocking a popup on an empty email body would be
  // demanding copy for a message that does not exist.

  if (usesPush(draft)) {
    requireText("title", "Title", TITLE_MAX, "Give the popup a title.");
    requireText("body", "Description", BODY_MAX, "Write a description.");

    if (!ROUTE_BY_ID[draft?.routeKey]) {
      push("routeKey", "Pick where the popup opens.");
    }

    if (!isPushSegment(draft?.pushSegment)) {
      // An email-only segment reaching this branch is the interesting case: a
      // popup aimed at people with no app is an empty send, so the picker
      // never offers those three and validation refuses them if they arrive.
      push("pushSegment", "Pick who receives the popup.");
    }

    // A `filtered` segment without an id targets nothing. The server would
    // reject it, but the author should not have to send it to find out.
    if (segmentNeedsFilter(draft?.pushSegment) && !draft?.pushFilterId) {
      push("pushFilterId", "Choose a saved filter for the popup audience.");
    }
  }

  if (usesEmail(draft)) {
    requireText("emailSubject", "Subject", TITLE_MAX, "Write a subject line.");

    // Asked of the TEXT, not the markup. A `contentEditable` the author focused
    // and then cleared leaves `<div><br></div>` behind, which trims to
    // non-empty — so a body check against the HTML would wave a blank email
    // through.
    if (isEmailBodyEmpty(draft?.emailBodyHtml)) {
      push("emailBodyHtml", "Write the email.");
    }

    if (!isEmailSegment(draft?.emailSegment)) {
      push("emailSegment", "Pick who receives the email.");
    }

    if (segmentNeedsFilter(draft?.emailSegment) && !draft?.emailFilterId) {
      push("emailFilterId", "Choose a saved filter for the email audience.");
    }

    // The CTA is a pair. Half of one is a button that goes nowhere, or a
    // destination with no label to click.
    const ctaText = String(draft?.emailCtaText || "").trim();
    const ctaUrl = String(draft?.emailCtaUrl || "").trim();
    if (ctaText && !ctaUrl) {
      push("emailCtaUrl", "Add a link for the button, or clear its label.");
    }
    if (ctaUrl && !ctaText) {
      push("emailCtaText", "Give the button a label, or clear its link.");
    }
  }

  if (draft?.scheduleMode === "later") {
    // The bug this guards: a blank date used to fall through to a null
    // `scheduled_at`, i.e. send immediately, while the UI said "scheduled".
    if (!draft.scheduledAtLocal) {
      push("scheduledAtLocal", "Pick a date and time.");
    } else {
      const when = new Date(draft.scheduledAtLocal);
      if (Number.isNaN(when.getTime())) {
        push("scheduledAtLocal", "That date isn't valid.");
      } else if (when.getTime() <= now.getTime()) {
        push("scheduledAtLocal", "Pick a time in the future.");
      }
    }
  }

  return errors;
};

/**
 * First error for a given field, or undefined.
 *
 * @param {Array<{ field: string, message: string }>} errors
 * @param {string} field
 * @returns {string|undefined}
 */
export const errorFor = (errors, field) =>
  errors?.find((error) => error.field === field)?.message;
